import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Container,
  Card,
  CardContent,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import {
  doc,
  getDoc,
  collection,
  writeBatch,
  serverTimestamp,
  query,
  where,
  getDocs,
  limit,
  increment,
} from 'firebase/firestore';

import { db } from '../config/firebase';
import { getTranslations } from './i18n';

import './SurveyResponse.css';
import '../Platform/system/survey/ui/backgroundStyles.css';
import IntroSurvey from './IntroSurvey';
import ThanksUser from './ThanksUser';
import ThanksUserResult from './ThanksUserResult';
import ProgressBarUser from './ProgressBarUser';
import PlatformFooter from './PlatformFooter';

import {
  AlreadySubmittedScreen,
  SurveySkeleton,
  SubmittingOverlay,
  PreparingResultScreen,
} from './SurveyStatusScreens';
import { getFormLayout, DEFAULT_SURVEY_TYPE as _DFT } from './SurveyFormLayouts';
import {
  COMMENT_MAX_LENGTH,
  THANKS_DELAY_MS,
  AUTO_ADVANCE_DELAY_MS,
  DEFAULT_SURVEY_TYPE,
  GLASS_SX,
  getOrCreateUserId,
  hasAlreadySubmitted,
  markAsSubmitted,
  initializeAnswers,
  isQuestionAnswered,
  useViewportHeight,
} from './surveyHelpers';

// ─── Main Component ───────────────────────────────────────────────────────────
const SurveyResponse = ({ urlId }) => {
  const t = useMemo(() => getTranslations(), []);

  // Fija --vh-100 con el alto real del viewport visual (ver surveyHelpers.js).
  // Hace que el layout stepped aproveche toda la pantalla disponible y evite
  // saltos cuando aparece el teclado en la pregunta de texto.
  useViewportHeight();

  const [survey, setSurvey]                     = useState(null);
  const [loading, setLoading]                   = useState(true);
  const [error, setError]                       = useState('');
  const [answers, setAnswers]                   = useState({});
  const [comment, setComment]                   = useState('');
  const [submitting, setSubmitting]             = useState(false);
  const [successMessage, setSuccessMessage]     = useState('');
  const [showValidation, setShowValidation]     = useState(false);
  const [submitSuccess, setSubmitSuccess]       = useState(false);
  const [logoUrl, setLogoUrl]                   = useState(null);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [fetchKey, setFetchKey]                 = useState(0);
  const [showThanks, setShowThanks]             = useState(false);
  const [preparingThanks, setPreparingThanks]   = useState(false);
  const [submittedDocData, setSubmittedDocData] = useState(null);
  const [surveyStarted, setSurveyStarted]       = useState(false);
  // Índice de la pregunta activa para layouts de un paso a la vez (isStepped)
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const userIdRef = useRef(getOrCreateUserId());
  const thanksTimeoutRef = useRef(null);
  const autoAdvanceTimeoutRef = useRef(null);

  // ── Derived state ──────────────────────────────────────────────────────────
  const sortedQuestions = useMemo(() => {
    if (!survey?.questions) return [];
    return Object.entries(survey.questions).sort(
      ([, a], [, b]) => (a.orden || 0) - (b.orden || 0)
    );
  }, [survey?.questions]);

  // Resuelve qué diseño usar según survey.type (0, 1, 2...). Si el type no
  // está registrado, cae al layout DEFAULT_SURVEY_TYPE.
  const layoutConfig = useMemo(
    () => getFormLayout(survey?.type ?? DEFAULT_SURVEY_TYPE),
    [survey?.type]
  );
  const FormLayout    = layoutConfig.Component;
  const isSteppedFlow = layoutConfig.isStepped;

  const currentQuestionEntry = sortedQuestions[currentStepIndex] || null;
  const isLastQuestion       = currentStepIndex >= sortedQuestions.length - 1;
  const isTextStep           = currentQuestionEntry?.[1]?.type === 'text';

  // En layouts "isStepped", el botón "Siguiente/Enviar" solo se muestra si:
  //   - no es un flujo stepped (siempre hay botón), o
  //   - es la última pregunta (ahí siempre hace falta el botón de enviar), o
  //   - la pregunta actual es de texto (no hay "seleccionar" que auto-avance)
  const showNextOrSubmitButton = !isSteppedFlow || isLastQuestion || isTextStep;

  const progress = useMemo(() => {
    if (!sortedQuestions.length) return 0;
    const answered = sortedQuestions.filter(([qId, q]) =>
      isQuestionAnswered(q, answers[qId])
    ).length;
    return Math.round((answered / sortedQuestions.length) * 100);
  }, [answers, sortedQuestions]);

  const unansweredQuestions = useMemo(() => {
    if (!showValidation || !sortedQuestions.length) return new Set();
    return new Set(
      sortedQuestions
        .filter(([qId, q]) => !isQuestionAnswered(q, answers[qId]))
        .map(([qId]) => qId)
    );
  }, [sortedQuestions, answers, showValidation]);

  // ── Validation ─────────────────────────────────────────────────────────────
  const validateForm = useCallback(() => {
    if (!survey) return false;
    return sortedQuestions.every(([qId, q]) => isQuestionAnswered(q, answers[qId]));
  }, [survey, sortedQuestions, answers]);

  // ── Survey fetch ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!urlId) return;

    if (fetchKey === 0 && hasAlreadySubmitted(urlId)) {
      setAlreadySubmitted(true);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchSurvey = async () => {
      setLoading(true);
      try {
        const q             = query(collection(db, 'surveys'), where('urlId', '==', urlId), limit(1));
        const querySnapshot = await getDocs(q);

        if (cancelled) return;

        if (querySnapshot.empty) { setError(t.surveyNotFound); setLoading(false); return; }

        const surveyDoc  = querySnapshot.docs[0];
        const surveyData = surveyDoc.data();

        if (!surveyData.isActive) { setError(t.surveyNotActive); setLoading(false); return; }

        const surveyWithId = { id: surveyDoc.id, ...surveyData };

        let resolvedLogoUrl = null;
        if (surveyData.showLogo && surveyData.createdBy) {
          try {
            const logoDoc = await getDoc(doc(db, 'Logo', surveyData.createdBy));
            if (logoDoc.exists()) resolvedLogoUrl = logoDoc.data().Url || null;
          } catch (logoErr) {
            console.warn('Could not load company logo:', logoErr);
          }
        }

        if (cancelled) return;

        setSurvey(surveyWithId);
        setAnswers(initializeAnswers(surveyData.questions));
        setCurrentStepIndex(0);
        if (resolvedLogoUrl) setLogoUrl(resolvedLogoUrl);
        setLoading(false);
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading survey:', err);
          setError(t.errorLoading);
          setLoading(false);
        }
      }
    };

    fetchSurvey();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlId, fetchKey]);

  useEffect(() => {
    if (showValidation && validateForm()) setShowValidation(false);
  }, [answers, showValidation, validateForm]);

  useEffect(() => {
    return () => {
      if (thanksTimeoutRef.current) clearTimeout(thanksTimeoutRef.current);
      if (autoAdvanceTimeoutRef.current) clearTimeout(autoAdvanceTimeoutRef.current);
    };
  }, []);

  // ── Bloqueo de refresh/pull-to-refresh mientras la encuesta está en curso ──
  // Se activa solo entre "el usuario ya presionó Comenzar" y "ya envió sus
  // respuestas", para no molestar en la pantalla de intro ni después de
  // terminar. Combina 3 defensas: overscroll-behavior (Chrome/Android),
  // bloqueo manual del gesto táctil (refuerzo para Safari/iOS), y el diálogo
  // nativo de confirmación al intentar recargar/cerrar (beforeunload).
  useEffect(() => {
    if (!surveyStarted || submitSuccess) return;

    let touchStartY = 0;

    const handleTouchStart = (e) => {
      touchStartY = e.touches[0].clientY;
    };

    const handleTouchMove = (e) => {
      const touchY = e.touches[0].clientY;
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      // Si el usuario está en el tope de la página y desliza hacia abajo,
      // es un gesto de "pull-to-refresh": lo bloqueamos.
      if (scrollTop <= 0 && touchY > touchStartY) {
        e.preventDefault();
      }
    };

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = ''; // requerido por algunos navegadores para mostrar el diálogo nativo
      return '';
    };

    document.body.style.overscrollBehaviorY = 'contain';
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.body.style.overscrollBehaviorY = '';
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [surveyStarted, submitSuccess]);

  // ── Avance automático (layouts isStepped) ──────────────────────────────────
  // `autoAdvancePending` distingue dos situaciones que antes se confundían:
  //   1. El usuario acaba de contestar la pregunta activa -> ya hay un
  //      auto-avance programado, así que la flechita "continuar" NO debe
  //      mostrarse (antes aparecía un instante y desaparecía al avanzar,
  //      un parpadeo sin sentido ya que el auto-avance hace el trabajo).
  //   2. El usuario volvió (vía burbuja de resumen) a una pregunta que YA
  //      estaba contestada de antes -> ahí no hay ningún auto-avance
  //      programado (scheduleAutoAdvance solo se llama al contestar), así
  //      que la flechita SÍ debe mostrarse para poder seguir manualmente.
  const [autoAdvancePending, setAutoAdvancePending] = useState(false);

  // Se resetea cada vez que cambia el paso activo: al llegar a una pregunta
  // nueva (por auto-avance o por navegación manual) todavía no hay ningún
  // auto-avance programado para ELLA, sin importar lo que haya pasado en
  // la pregunta anterior.
  useEffect(() => {
    setAutoAdvancePending(false);
  }, [currentStepIndex]);

  const scheduleAutoAdvance = useCallback((qId) => {
    if (!isSteppedFlow) return;
    if (!currentQuestionEntry || currentQuestionEntry[0] !== qId) return;
    if (isLastQuestion) return;

    if (autoAdvanceTimeoutRef.current) clearTimeout(autoAdvanceTimeoutRef.current);
    setAutoAdvancePending(true);
    autoAdvanceTimeoutRef.current = setTimeout(() => {
      setShowValidation(false);
      setCurrentStepIndex((i) => Math.min(i + 1, sortedQuestions.length - 1));
    }, AUTO_ADVANCE_DELAY_MS);
  }, [isSteppedFlow, currentQuestionEntry, isLastQuestion, sortedQuestions.length]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleRatingChange = useCallback((qId, v) => {
    setAnswers((p) => ({ ...p, [qId]: v }));
    scheduleAutoAdvance(qId);
  }, [scheduleAutoAdvance]);

  const handleSliderChange = useCallback(
    (qId, v) => setAnswers((p) => ({ ...p, [qId]: v })), []
  );

  const handleSliderCommit = useCallback((qId) => {
    scheduleAutoAdvance(qId);
  }, [scheduleAutoAdvance]);

  const handleTextChange = useCallback(
    (qId, e) => setAnswers((p) => ({ ...p, [qId]: e.target.value })), []
  );

  const handleRadioChange = useCallback((qId, e) => {
    const value = e.target.value;
    setAnswers((p) => ({ ...p, [qId]: value }));
    scheduleAutoAdvance(qId);
  }, [scheduleAutoAdvance]);

  const handleEmojiChange = useCallback((qId, emoji) => {
    const numericValue = emoji.match(/face(\d+)/)?.[1] ?? emoji;
    setAnswers((p) => ({ ...p, [qId]: numericValue }));
    scheduleAutoAdvance(qId);
  }, [scheduleAutoAdvance]);

  const handleCommentChange = useCallback((e) => {
    if (e.target.value.length <= COMMENT_MAX_LENGTH) setComment(e.target.value);
  }, []);

  const handleStartSurvey = useCallback(() => {
    setSurveyStarted(true);
  }, []);

  // ── Navegación de layouts isStepped ─────────────────────────────────────────
  const handleStepNext = useCallback(() => {
    if (!currentQuestionEntry) return;
    const [qId, q] = currentQuestionEntry;
    if (!isQuestionAnswered(q, answers[qId])) {
      setShowValidation(true);
      setError(t.questionRequired || t.validateAll);
      return;
    }
    setShowValidation(false);
    setCurrentStepIndex((i) => Math.min(i + 1, sortedQuestions.length - 1));
  }, [currentQuestionEntry, answers, sortedQuestions.length, t]);

  const handleGoToStep = useCallback((idx) => {
    if (idx > currentStepIndex) {
      const answered =
        currentQuestionEntry &&
        isQuestionAnswered(currentQuestionEntry[1], answers[currentQuestionEntry[0]]);
      if (!answered) {
        setShowValidation(true);
        setError(t.questionRequired || t.validateAll);
        return;
      }
    }

    if (autoAdvanceTimeoutRef.current) clearTimeout(autoAdvanceTimeoutRef.current);
    setShowValidation(false);
    setCurrentStepIndex(idx);
  }, [currentStepIndex, currentQuestionEntry, answers, t]);

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (isSteppedFlow && !isLastQuestion) {
      handleStepNext();
      return;
    }

    if (hasAlreadySubmitted(urlId)) { setAlreadySubmitted(true); return; }

    if (!validateForm()) {
      setShowValidation(true);
      setError(t.validateAll);
      return;
    }

    setShowValidation(false);
    setSubmitting(true);

    try {
      const now     = new Date();
      const year    = now.getFullYear();
      const month   = now.getMonth() + 1;
      const yyyy_mm = `${year}_${String(month).padStart(2, '0')}`;

      const answerMap = {};
      sortedQuestions.forEach(([qId, q]) => {
        const orden = q.orden || 0;
        answerMap[`q${orden}`] = { ans: answers[qId] ?? null, orden };
      });

      const docData = {
        addRec:    serverTimestamp(),
        answer:    answerMap,
        month,
        anio:      year,
        createdBy: survey.createdBy || '',
        uid:       userIdRef.current,
      };
      if (comment.trim()) docData.comm = comment.trim();

      // Escritura por lote: antes se hacía 1 addDoc + N setDoc (uno por
      // pregunta) como llamadas de red separadas y sin atomicidad — si una
      // fallaba a la mitad, las respuestas quedaban guardadas a medias. Un
      // solo writeBatch agrupa todo en una sola operación atómica.
      const batch = writeBatch(db);

      const answerDocRef = doc(collection(db, 'surveys', survey.id, 'answers'));
      batch.set(answerDocRef, docData);

      sortedQuestions.forEach(([qId, q]) => {
        // El slider no encaja en el esquema ans_1..ans_5 sin importar su
        // valor: su rango (min/max) es configurable y no representa una
        // escala fija, aunque el valor puntual caiga entre 1 y 5.
        if (q.type === 'slider') return;

        const orden  = q.orden || 0;
        const ansNum = parseInt(answers[qId], 10);

        // El resumen ans_1..ans_5 solo tiene sentido para respuestas en
        // escala 1-5 (rating, emojis, radio — que siempre tiene id entero
        // y máximo 5 opciones). Antes se escribía igual para preguntas de
        // texto, generando documentos vacíos sin valor para el reporte.
        if (!Number.isInteger(ansNum) || ansNum < 1 || ansNum > 5) return;

        const sumRef = doc(db, 'surveys', survey.id, 'answerSum', `${yyyy_mm}_q${orden}`);
        batch.set(
          sumRef,
          {
            text:      q.text || '',
            orden,
            month,
            anio:      year,
            createdBy: survey.createdBy || '',
            ans_1: increment(ansNum === 1 ? 1 : 0),
            ans_2: increment(ansNum === 2 ? 1 : 0),
            ans_3: increment(ansNum === 3 ? 1 : 0),
            ans_4: increment(ansNum === 4 ? 1 : 0),
            ans_5: increment(ansNum === 5 ? 1 : 0),
          },
          { merge: true }
        );
      });

      await batch.commit();

      markAsSubmitted(urlId);
      setSuccessMessage(t.thankYou);
      setSubmitSuccess(true);
      setSubmittedDocData(docData);
      setPreparingThanks(true);

      thanksTimeoutRef.current = setTimeout(() => {
        setShowThanks(true);
        setPreparingThanks(false);
      }, THANKS_DELAY_MS);
    } catch (err) {
      console.error('Error submitting answers:', err);
      setError(t.errorSubmitting);
      setSubmitSuccess(false);
      setPreparingThanks(false);
    } finally {
      setSubmitting(false);
    }
  }, [survey, sortedQuestions, answers, comment, validateForm, urlId, t, isSteppedFlow, isLastQuestion, handleStepNext]);

  // ── Misc handlers ──────────────────────────────────────────────────────────
  const handleCloseError   = useCallback(() => setError(''), []);
  const handleCloseSuccess = useCallback(() => setSuccessMessage(''), []);

  const handleRetake = useCallback(() => {
    try { localStorage.removeItem(`survey_submitted_${urlId}`); } catch {}
    setAlreadySubmitted(false);
    setSubmitSuccess(false);
    setShowThanks(false);
    setPreparingThanks(false);
    setSubmittedDocData(null);
    setComment('');
    setShowValidation(false);
    setError('');
    setSurveyStarted(false);
    setCurrentStepIndex(0);
    if (survey) {
      setAnswers(initializeAnswers(survey.questions));
    } else {
      // Pasa cuando se llega acá después de un reload: el efecto de fetch
      // corta camino apenas ve hasAlreadySubmitted(urlId) en true y nunca
      // llega a pedir el documento, así que `survey` sigue en null en este
      // punto. Sin este setLoading(true), el próximo render no matchea
      // ningún return temprano (loading ya es false, alreadySubmitted ya
      // se puso en false acá arriba, y `survey && !surveyStarted` es falso
      // porque survey es null) y cae al formulario principal con `survey`
      // todavía null, reventando en `survey.showComment`. Forzando loading
      // acá se muestra el skeleton mientras corre el re-fetch disparado
      // por fetchKey.
      setLoading(true);
      setFetchKey((k) => k + 1);
    }
  }, [urlId, survey]);

  // ── Early returns ──────────────────────────────────────────────────────────
  if (showThanks) {
    return survey?.showResult
      ? <ThanksUserResult docData={submittedDocData} resultRules={survey?.resultRules || []} />
      : <ThanksUser docData={submittedDocData} />;
  }

  if (preparingThanks) {
    return <PreparingResultScreen willShowResult={!!survey?.showResult} t={t} />;
  }

  if (loading) return <SurveySkeleton />;

  if (alreadySubmitted)
    return <AlreadySubmittedScreen surveyTitle={survey?.title} onRetake={handleRetake} t={t} />;

  if (error && !survey)
    return (
      <Container maxWidth={false} disableGutters className="error-container">
        <Alert severity="error" className="error-alert">{error}</Alert>
        <Button variant="outlined" component="a" href="/">{t.backToHome}</Button>
      </Container>
    );

  if (survey && !surveyStarted) {
    return (
      <Container maxWidth={false} disableGutters className="main-container">
        <IntroSurvey
          logoUrl={logoUrl}
          title={survey.title}
          description={survey.description}
          buttonText={t.startSurvey || 'Comenzar ahora'}
          surveyStyle={survey.style}
          onStart={handleStartSurvey}
        />
        <PlatformFooter t={t} />
      </Container>
    );
  }

  // Optional chaining como defensa adicional: si por algún motivo se llega
  // acá con `survey` todavía null (no debería pasar con el fix de
  // handleRetake de arriba), evita el mismo TypeError en vez de romper el
  // render.
  const showCommentBlock = survey?.showComment && (!isSteppedFlow || isLastQuestion);
  const showCompleteAllHint = (!isSteppedFlow || isLastQuestion) && progress < 100 && !submitting && !submitSuccess;

  // Layout clásico (type 0) listo para enviar: todas las preguntas
  // contestadas (progress === 100%, el mismo criterio que habilita el
  // botón más abajo). A partir de ahí el botón se vuelve "fixed" (igual
  // criterio que .stepped-fixed-submit) para que no se oculte aunque el
  // usuario siga deslizando la pantalla hacia arriba o abajo revisando
  // sus respuestas antes de confirmar.
  const classicFixedSubmit = !isSteppedFlow && progress === 100;

  // ── Main render ────────────────────────────────────────────────────────────
  return (
    <Container maxWidth={false} disableGutters className="main-container">
      <SubmittingOverlay
        open={submitting}
        message={t.savingAnswers || 'Saving your answers..'}
      />

      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseError}>
        <Alert onClose={handleCloseError} severity="error" className="alert">{error}</Alert>
      </Snackbar>
      <Snackbar open={!!successMessage} autoHideDuration={6000} onClose={handleCloseSuccess}>
        <Alert onClose={handleCloseSuccess} severity="success" className="alert">{successMessage}</Alert>
      </Snackbar>

      {survey && <ProgressBarUser progress={progress} label={t.progress} />}

      {survey && (
        <div
          className={survey.style ?? 'bg-white'}
          style={{ width: '100%', borderRadius: '8px', overflow: 'hidden' }}
        >
          <Box component="form" onSubmit={handleSubmit} noValidate className="survey-form">
            <Paper
              elevation={3}
              className={`survey-paper survey-paper-with-sticky-progress${classicFixedSubmit ? ' survey-paper-with-fixed-submit' : ''}`}
              sx={{ background: 'transparent' }}
            >
              {/* Zona de preguntas: el diseño depende únicamente de survey.type */}
              <FormLayout
                survey={survey}
                sortedQuestions={sortedQuestions}
                answers={answers}
                currentStepIndex={currentStepIndex}
                currentQuestionEntry={currentQuestionEntry}
                isLastQuestion={isLastQuestion}
                unansweredQuestions={unansweredQuestions}
                onRatingChange={handleRatingChange}
                onSliderChange={handleSliderChange}
                onSliderCommit={handleSliderCommit}
                onTextChange={handleTextChange}
                onRadioChange={handleRadioChange}
                onEmojiChange={handleEmojiChange}
                onGoToStep={handleGoToStep}
                autoAdvancePending={autoAdvancePending}
                t={t}
              />

              {showCommentBlock && (
                <Card
                  variant="outlined"
                  className="user-info-card"
                  sx={{ ...GLASS_SX, borderColor: 'secondary.light' }}
                >
                  <CardContent>
                    <Typography variant="h6" className="user-info-title">
                      {t.additionalComments}
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      variant="outlined"
                      placeholder={t.commentsPlaceholder}
                      value={comment}
                      onChange={handleCommentChange}
                      inputProps={{ maxLength: COMMENT_MAX_LENGTH }}
                      className="user-info-textfield"
                      sx={{ mt: 2 }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                      <Typography
                        variant="caption"
                        color={comment.length >= COMMENT_MAX_LENGTH ? 'error' : 'text.secondary'}
                      >
                        {comment.length} / {COMMENT_MAX_LENGTH}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              )}

             <Box
                className={`submit-button-container${isSteppedFlow ? ' stepped-fixed-submit' : ''}${classicFixedSubmit ? ' classic-fixed-submit' : ''}`}
              >
                {showNextOrSubmitButton && (
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={
                      submitting ||
                      submitSuccess ||
                      (isSteppedFlow
                        ? !isQuestionAnswered(
                            currentQuestionEntry?.[1] || {},
                            answers[currentQuestionEntry?.[0]]
                          )
                        : progress < 100)
                    }
                    endIcon={
                      submitting
                        ? <CircularProgress size={24} color="inherit" />
                        : (isSteppedFlow && !isLastQuestion) ? null : <SendIcon />
                    }
                    className="submit-button"
                  >
                    {submitSuccess
                      ? t.submitted
                      : submitting
                      ? t.submitting
                      : (isSteppedFlow && !isLastQuestion)
                      ? (t.nextQuestion || 'Siguiente →')
                      : t.submitButton}
                  </Button>
                )}

                {showCompleteAllHint && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: 'block', textAlign: 'center' }}
                  >
                    {t.completeAll}
                  </Typography>
                )}
              </Box>
            </Paper>
          </Box>
        </div>
      )}

      <PlatformFooter t={t} />
    </Container>
  );
};

export default SurveyResponse;